import { Checkbox, Tooltip, useDraggable } from '@pique/component';
import { WorkspacePropertiesAdapter } from '@pique/core/modules/properties';
import { TagService } from '@pique/core/modules/tag';
import type { AffineDNDData } from '@pique/core/types/dnd';
import { stopPropagation } from '@pique/core/utils';
import { i18nTime } from '@pique/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import type { PropsWithChildren, Ref } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';

import { WorkbenchLink } from '../../../modules/workbench/view/workbench-link';
import { AttendeeAvatars } from '../../pique/page-properties/attendee-avatars';
import {
  anchorIndexAtom,
  rangeIdsAtom,
  selectionStateAtom,
  useAtom,
} from '../scoped-atoms';
import type { PageListItemProps } from '../types';
import { useAllDocDisplayProperties } from '../use-all-doc-display-properties';
import { ColWrapper } from '../utils';
import * as styles from './page-list-item.css';
import { PageTags } from './page-tags';

const ListTitleCell = memo(function ListTitleCell({
  title,
  createDate,
  pageId,
}: Pick<PageListItemProps, 'title' | 'createDate' | 'pageId'>) {
  const propertiesAdapter = useService(WorkspacePropertiesAdapter);
  const attendees = propertiesAdapter.getPageAttendees(pageId);

  return (
    <div data-testid="page-list-item-title" className={styles.titleCell}>
      <div className={styles.titleCellMain}>
        <div className={styles.titleAttendeeRow}>
          <div
            data-testid="page-list-item-title-text"
            className={styles.titleWithAttendees}
          >
            {title}
          </div>
          {attendees.length > 0 && (
            <AttendeeAvatars
              attendees={attendees}
              maxDisplay={3}
              size="small"
            />
          )}
        </div>
      </div>
      <div className={styles.titleMetadata}>
        {createDate && <PageCreateDateCell createDate={createDate} />}
      </div>
    </div>
  );
});

const ListIconCell = memo(function ListIconCell({
  icon,
}: Pick<PageListItemProps, 'icon'>) {
  return (
    <div data-testid="page-list-item-icon" className={styles.iconCell}>
      {icon}
    </div>
  );
});

const PageSelectionCell = memo(function PageSelectionCell({
  selectable,
  onSelectedChange,
  selected,
}: Pick<PageListItemProps, 'selectable' | 'onSelectedChange' | 'selected'>) {
  const onSelectionChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>) => {
      return onSelectedChange?.();
    },
    [onSelectedChange]
  );
  if (!selectable) {
    return null;
  }
  return (
    <div className={styles.selectionCell}>
      <Checkbox
        onClick={stopPropagation}
        checked={!!selected}
        onChange={onSelectionChange}
      />
    </div>
  );
});

export const PageTagsCell = memo(function PageTagsCell({
  pageId,
}: Pick<PageListItemProps, 'pageId'>) {
  const tagList = useService(TagService).tagList;
  const tags = useLiveData(tagList.tagsByPageId$(pageId));

  return (
    <div data-testid="page-list-item-tags" className={styles.tagsCell}>
      <PageTags
        tags={tags}
        hoverExpandDirection="left"
        widthOnHover="300%"
        maxItems={2}
      />
    </div>
  );
});

const formatShortDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const PageCreateDateCell = memo(function PageCreateDateCell({
  createDate,
}: Pick<PageListItemProps, 'createDate'>) {
  return (
    <Tooltip content={i18nTime(createDate)}>
      <div
        data-testid="page-list-item-date"
        data-date-raw={createDate}
        className={styles.dateCell}
      >
        {formatShortDate(createDate)}
      </div>
    </Tooltip>
  );
});

const PageListOperationsCell = memo(function PageListOperationsCell({
  operations,
}: Pick<PageListItemProps, 'operations'>) {
  return operations ? (
    <div onClick={stopPropagation} className={styles.operationsCell}>
      {operations}
    </div>
  ) : null;
});

export const PageListItem = memo(function PageListItem(
  props: PageListItemProps
) {
  const [displayProperties] = useAllDocDisplayProperties();
  const pageTitleElement = useMemo(() => {
    return (
      <div className={styles.dragPageItemOverlay}>
        <div className={styles.titleIconsWrapper}>
          <PageSelectionCell
            onSelectedChange={props.onSelectedChange}
            selectable={props.selectable}
            selected={props.selected}
          />
          <ListIconCell icon={props.icon} />
        </div>
        <ListTitleCell
          title={props.title}
          createDate={props.createDate}

          pageId={props.pageId}
        />
      </div>
    );
  }, [
    props.icon,
    props.onSelectedChange,
    props.selectable,
    props.selected,
    props.title,
    props.createDate,
    props.pageId,
  ]);

  const { dragRef, CustomDragPreview, dragging } = useDraggable<AffineDNDData>(
    () => ({
      canDrag: props.draggable,
      data: {
        entity: {
          type: 'doc',
          id: props.pageId,
        },
        from: {
          at: 'all-docs:list',
        },
      },
    }),
    [props.draggable, props.pageId]
  );

  return (
    <>
      <PageListItemWrapper
        ref={dragRef}
        to={props.to}
        pageId={props.pageId}
        pageIds={props.pageIds || []}
        onClick={props.onClick}
        draggable={props.draggable}
        isDragging={dragging}
      >
        <div className={styles.titleIconsWrapper}>
          <PageSelectionCell
            selectable={props.selectable}
            onSelectedChange={props.onSelectedChange}
            selected={props.selected}
          />
          <ListIconCell icon={props.icon} />
        </div>
        <div style={{ flex: 7, overflow: 'visible', minWidth: 0 }}>
          <ListTitleCell
            title={props.title}
            createDate={props.createDate}
  
            pageId={props.pageId}
          />
        </div>
        {displayProperties.displayProperties.tags && (
          <div style={{ flex: '0 1 auto', overflow: 'visible', minWidth: 0 }}>
            <PageTagsCell pageId={props.pageId} />
          </div>
        )}
        {props.operations ? (
          <ColWrapper
            className={styles.actionsCellWrapper}
            flex={1}
            alignment="end"
          >
            <PageListOperationsCell operations={props.operations} />
          </ColWrapper>
        ) : null}
      </PageListItemWrapper>
      <CustomDragPreview position="pointer-outside">
        {pageTitleElement}
      </CustomDragPreview>
    </>
  );
});

type PageListWrapperProps = PropsWithChildren<
  Pick<PageListItemProps, 'to' | 'pageId' | 'onClick' | 'draggable'> & {
    isDragging: boolean;
    pageIds: string[];
  }
>;

const PageListItemWrapper = memo(
  function PageListItemWrapper({
    ref,
    to,
    isDragging,
    pageId,
    pageIds,
    onClick,
    children,
    draggable,
  }: PageListWrapperProps & { ref?: Ref<HTMLAnchorElement & HTMLDivElement> }) {
    const [selectionState, setSelectionActive] = useAtom(selectionStateAtom);
    const [anchorIndex, setAnchorIndex] = useAtom(anchorIndexAtom);
    const [rangeIds, setRangeIds] = useAtom(rangeIdsAtom);

    const handleShiftClick = useCallback(
      (currentIndex: number) => {
        if (anchorIndex === undefined) {
          setAnchorIndex(currentIndex);
          onClick?.();
          return;
        }

        const lowerIndex = Math.min(anchorIndex, currentIndex);
        const upperIndex = Math.max(anchorIndex, currentIndex);
        const newRangeIds = pageIds.slice(lowerIndex, upperIndex + 1);

        const currentSelected = selectionState.selectedIds || [];

        // Set operations
        const setRange = new Set(rangeIds);
        const newSelected = new Set(
          currentSelected.filter(id => !setRange.has(id)).concat(newRangeIds)
        );

        selectionState.onSelectedIdsChange?.([...newSelected]);
        setRangeIds(newRangeIds);
      },
      [
        anchorIndex,
        onClick,
        pageIds,
        selectionState,
        setAnchorIndex,
        rangeIds,
        setRangeIds,
      ]
    );

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (!selectionState.selectable) {
          return;
        }
        e.stopPropagation();
        const currentIndex = pageIds.indexOf(pageId);

        if (e.shiftKey) {
          e.preventDefault();
          if (!selectionState.selectionActive) {
            setSelectionActive(true);
            setAnchorIndex(currentIndex);
            onClick?.();
          } else {
            handleShiftClick(currentIndex);
          }
        } else {
          setAnchorIndex(undefined);
          setRangeIds([]);
          onClick?.();
          return;
        }
      },
      [
        handleShiftClick,
        onClick,
        pageId,
        pageIds,
        selectionState.selectable,
        selectionState.selectionActive,
        setAnchorIndex,
        setRangeIds,
        setSelectionActive,
      ]
    );

    const commonProps = useMemo(
      () => ({
        'data-testid': 'page-list-item',
        'data-page-id': pageId,
        'data-draggable': draggable,
        className: styles.root,
        'data-clickable': !!onClick || !!to,
        'data-dragging': isDragging,
        onClick: onClick ? handleClick : undefined,
      }),
      [pageId, draggable, onClick, to, isDragging, handleClick]
    );

    useEffect(() => {
      if (selectionState.selectionActive) {
        // listen for shift key up
        const handleKeyUp = (e: KeyboardEvent) => {
          if (e.key === 'Shift') {
            setAnchorIndex(undefined);
            setRangeIds([]);
          }
        };
        window.addEventListener('keyup', handleKeyUp);
        return () => {
          window.removeEventListener('keyup', handleKeyUp);
        };
      }
      return;
    }, [
      selectionState.selectionActive,
      setAnchorIndex,
      setRangeIds,
      setSelectionActive,
    ]);

    if (to) {
      return (
        <WorkbenchLink ref={ref} {...commonProps} to={to}>
          {children}
        </WorkbenchLink>
      );
    } else {
      return (
        <div ref={ref} {...commonProps}>
          {children}
        </div>
      );
    }
  }
);
